import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eventFormSchema, type EventFormData } from '@/schemas/eventSchemas';
import type { Event } from '@/types/legacy';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

interface EventFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: Event | null;
  onSuccess?: () => void;
}

export function EventFormDialog({ open, onOpenChange, event, onSuccess }: EventFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const { data: users } = useQuery({
    queryKey: ['event-users'],
    queryFn: async () => {
      const { data, error } = await api
        .from('users')
        .select('id, email, full_name, is_active')
        .order('full_name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  const form = useForm<EventFormData>({
    resolver: zodResolver(eventFormSchema),
    values: {
      title: event?.title || '',
      description: event?.description || '',
      event_type: event?.event_type || 'other',
      start_date: event?.start_date ? event.start_date.slice(0, 16) : '',
      end_date: event?.end_date ? event.end_date.slice(0, 16) : '',
      all_day: event?.all_day ?? false,
      location: event?.location || '',
      priority: event?.priority || 0,
      status: event?.status || 'scheduled',
      related_to_type: event?.related_to_type || '',
      related_to_id: event?.related_to_id || '',
      reminder_minutes: event?.reminder_minutes,
      attendees: event?.attendees || [],
      is_general: event?.is_general ?? true,
      visible_to_user_ids: event?.visible_to_user_ids || [],
      is_active: event?.is_active ?? true,
    },
  });
  const isGeneral = form.watch('is_general');

  const toggleVisibleUser = (userId: string, checked: boolean) => {
    const current = form.getValues('visible_to_user_ids') || [];
    const next = checked
      ? Array.from(new Set([...current, userId]))
      : current.filter((id) => id !== userId);
    form.setValue('visible_to_user_ids', next, { shouldValidate: true, shouldDirty: true });
  };

  const onSubmit = async (data: EventFormData) => {
    setLoading(true);
    try {
      const payload = {
        title: data.title,
        description: data.description || null,
        event_type: data.event_type,
        start_date: data.start_date,
        end_date: data.end_date || null,
        all_day: data.all_day,
        location: data.location || null,
        priority: data.priority,
        status: data.status,
        related_to_type: data.related_to_type || null,
        related_to_id: data.related_to_id || null,
        reminder_minutes: data.reminder_minutes || null,
        attendees: data.attendees || [],
        is_general: data.is_general,
        visible_to_user_ids: data.is_general ? null : data.visible_to_user_ids,
        is_active: data.is_active,
      };

      if (event) {
        const { error } = await api
          .from('events')
          .update(payload)
          .eq('id', event.id);

        if (error) throw error;

        toast({
          title: 'Evento actualizado',
          description: 'El evento se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('events')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Evento creado',
          description: 'El evento se creó correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['events'] });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el evento',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Título del evento" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descripción del evento" rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="event_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Evento</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="maintenance">Mantenimiento</SelectItem>
                        <SelectItem value="meeting">Reunión</SelectItem>
                        <SelectItem value="deadline">Fecha Límite</SelectItem>
                        <SelectItem value="training">Capacitación</SelectItem>
                        <SelectItem value="production">Producción</SelectItem>
                        <SelectItem value="other">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar estado" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="scheduled">Programado</SelectItem>
                        <SelectItem value="in_progress">En Progreso</SelectItem>
                        <SelectItem value="completed">Completado</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha y Hora de Inicio *</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha y Hora de Fin</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="all_day"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Evento de todo el día</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El evento dura todo el día
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_general"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Evento general</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Visible para todos los usuarios
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (checked) {
                          form.setValue('visible_to_user_ids', [], { shouldValidate: true });
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {!isGeneral && (
              <FormField
                control={form.control}
                name="visible_to_user_ids"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usuarios con acceso</FormLabel>
                    <FormDescription>
                      Selecciona los usuarios que podran ver el evento.
                    </FormDescription>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                      {users?.map((user) => {
                        const baseLabel = user.full_name || user.email;
                        const label = user.is_active ? baseLabel : `${baseLabel} (inactivo)`;
                        return (
                          <label key={user.id} className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={field.value?.includes(user.id)}
                              onCheckedChange={(checked) => toggleVisibleUser(user.id, Boolean(checked))}
                            />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ubicación</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ej: Sala de reuniones, Planta de producción" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prioridad: {field.value}</FormLabel>
                  <FormControl>
                    <Slider
                      min={0}
                      max={10}
                      step={1}
                      value={[field.value]}
                      onValueChange={(vals) => field.onChange(vals[0])}
                      className="mt-2"
                    />
                  </FormControl>
                  <FormDescription>
                    0 = Baja prioridad, 10 = Alta prioridad
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="related_to_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Relación</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: work_order, machine, project" />
                    </FormControl>
                    <FormDescription>
                      Tipo de entidad relacionada (opcional)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="related_to_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID de Relación</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="UUID del elemento relacionado" />
                    </FormControl>
                    <FormDescription>
                      ID de la entidad relacionada (opcional)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="reminder_minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recordatorio (minutos antes)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      min={0}
                      placeholder="Ej: 15, 30, 60"
                    />
                  </FormControl>
                  <FormDescription>
                    Minutos antes del evento para enviar recordatorio
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El evento está activo en el sistema
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {event ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
